import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError, downloadSigned } from "@/lib/api";
import { formatCZK, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReceiptText, FileText, Check, X } from "lucide-react";
import { toast } from "sonner";

// Faktury subdodavatelů čekající na schválení — blok na pracovním Přehledu.
// Schválením se faktura přiřadí k zakázce a vznikne doklad v ekonomice (Subdodávka).
// headless (236): bez seznamu, jen okna — úvod na počítači má řádky v pruhu „Faktury
// a sklad“ a schvalovací okno otevře přes openId; zamítnutí je pak přímo v okně.
export default function SubInvoiceApproval({ openId, onOpened, onChanged, headless = false }) {
  const [rows, setRows] = useState(null);
  const [projects, setProjects] = useState([]);
  const [approve, setApprove] = useState(null); // {inv, project_id}
  const [reject, setReject] = useState(null);   // {inv, reason}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/sub-invoices").then((r) => setRows((r.data || []).filter((i) => i.status === "pending"))).catch(() => setRows([]));
    api.get("/projects/options").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // 158/B4: proklik z „Co hoří" (?subinvoice=id) otevře rovnou schvalovací dialog.
  const retried = useRef(null);
  useEffect(() => {
    if (!openId || !rows) return;
    const inv = rows.find((i) => i.id === openId);
    // faktura přišla až po načtení seznamu — jednou načíst znovu, pak teprve vzdát
    if (!inv && retried.current !== openId) { retried.current = openId; load(); return; }
    if (inv) setApprove({ inv, project_id: inv.suggested_project_id || "" });
    onOpened?.();   // spotřebuj param, ať se dialog neotevírá po dalším renderu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, rows]);

  const doApprove = async () => {
    if (!approve?.project_id) return toast.error("Vyberte zakázku.");
    setBusy(true);
    try {
      await api.post(`/sub-invoices/${approve.inv.id}/approve`, { project_id: approve.project_id });
      toast.success("Faktura schválena a přiřazena k zakázce.");
      setApprove(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!(reject?.reason || "").trim()) return toast.error("Uveďte důvod zamítnutí.");
    setBusy(true);
    try {
      await api.post(`/sub-invoices/${reject.inv.id}/reject`, { reason: reject.reason.trim() });
      toast.success("Faktura zamítnuta.");
      setReject(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!rows || (rows.length === 0 && !headless)) return null;

  const list = (
    <>
      <h2 className="font-bold flex items-center gap-2 mb-3"><ReceiptText size={18} className="text-brand" /> Faktury subdodavatelů ke schválení ({rows.length})</h2>
      <div className="space-y-2">
        {rows.map((inv) => (
          <div key={inv.id} data-testid={`sub-invoice-${inv.id}`}
            className="flex items-center gap-3 p-3 rounded-[14px] border border-border flex-wrap">
            <button onClick={() => downloadSigned(inv.file_url, `faktura-${inv.user_name}`)}
              className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`sub-invoice-open-${inv.id}`}>
              <FileText size={18} className="text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold truncate">{inv.user_name} · <span className="tabular-nums">{formatCZK(inv.amount)}</span></div>
                <div className="text-xs text-muted-foreground truncate">
                  {formatDate(inv.created_at)}
                  {inv.suggested_project_name ? ` · návrh zakázky: ${inv.suggested_project_name}` : ""}
                  {inv.note ? ` · ${inv.note}` : ""}
                </div>
              </div>
            </button>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button size="sm" onClick={() => setApprove({ inv, project_id: inv.suggested_project_id || "" })}
                data-testid={`sub-invoice-approve-${inv.id}`} className="h-9 flex-1 sm:flex-none font-bold bg-success hover:bg-success/90 text-white">
                <Check size={14} /> Schválit
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReject({ inv, reason: "" })}
                data-testid={`sub-invoice-reject-${inv.id}`} className="h-9 flex-1 sm:flex-none text-destructive hover:text-destructive">
                <X size={14} /> Zamítnout
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
  const dialogs = (
    <>

      {/* Approve dialog — přiřazení k zakázce */}
      <Dialog open={!!approve} onOpenChange={(o) => !o && setApprove(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schválit fakturu</DialogTitle><DialogDescription className="sr-only">Přiřazení faktury k zakázce</DialogDescription></DialogHeader>
          {approve && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{approve.inv.user_name} · <b className="text-foreground">{formatCZK(approve.inv.amount)}</b></p>

              {approve.inv.ocr && (
                <div className="rounded-[14px] border border-border bg-muted/30 p-3 text-sm" data-testid="sub-invoice-ocr-detail">
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Načteno z faktury</div>
                  {approve.inv.ocr.supplier_name && <div className="font-semibold truncate">{approve.inv.ocr.supplier_name}</div>}
                  <div className="mt-1 space-y-0.5 text-muted-foreground tabular-nums">
                    {Number(approve.inv.ocr.total_without_vat) > 0 && <div className="flex justify-between"><span>Bez DPH</span><span>{formatCZK(Number(approve.inv.ocr.total_without_vat))}</span></div>}
                    {Number(approve.inv.ocr.vat_amount) > 0 && <div className="flex justify-between"><span>DPH</span><span>{formatCZK(Number(approve.inv.ocr.vat_amount))}</span></div>}
                    {Number(approve.inv.ocr.total_with_vat) > 0 && <div className="flex justify-between font-semibold text-foreground"><span>Celkem</span><span>{formatCZK(Number(approve.inv.ocr.total_with_vat))}</span></div>}
                  </div>
                  {(approve.inv.ocr.line_items || []).length > 0 && (
                    <p className="text-[11px] mt-1.5 text-muted-foreground">{approve.inv.ocr.line_items.length} položek se přepíše do dokladu — rozdělit po zakázkách můžete v Ekonomice → Doklady.</p>
                  )}
                </div>
              )}

              <div>
                <Label>Zakázka</Label>
                <Select value={approve.project_id} onValueChange={(v) => setApprove({ ...approve, project_id: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="sub-invoice-project-select"><SelectValue placeholder="— vyberte zakázku —" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Vznikne doklad v ekonomice zakázky (kategorie Subdodávka), předvyplněný z faktury.</p>
              </div>
              <Button onClick={doApprove} disabled={busy} className="w-full h-11 font-bold" data-testid="sub-invoice-approve-confirm">Schválit a přiřadit</Button>
              {headless && (
                <Button variant="outline" disabled={busy} onClick={() => { const inv = approve.inv; setApprove(null); setTimeout(() => setReject({ inv, reason: "" }), 0); }}
                  className="w-full h-11 text-destructive hover:text-destructive" data-testid="sub-invoice-reject-from-approve">
                  <X size={14} /> Zamítnout
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog — povinný důvod */}
      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Zamítnout fakturu</DialogTitle><DialogDescription className="sr-only">Zamítnutí s důvodem</DialogDescription></DialogHeader>
          {reject && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{reject.inv.user_name} · <b className="text-foreground">{formatCZK(reject.inv.amount)}</b></p>
              <div><Label>Důvod zamítnutí</Label><Input value={reject.reason} onChange={(e) => setReject({ ...reject, reason: e.target.value })} className="h-11 mt-1" placeholder="např. chybí položky, špatná částka…" data-testid="sub-invoice-reject-reason" /></div>
              <Button variant="destructive" onClick={doReject} disabled={busy} className="w-full h-11 font-bold" data-testid="sub-invoice-reject-confirm">Zamítnout</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
  if (headless) return dialogs;
  return (
    <div className="bg-card border border-border rounded-[20px] p-4" data-testid="sub-invoice-approval">
      {list}
      {dialogs}
    </div>
  );
}
